import { convertToIsoDateTime } from '@pkg/utils';

import { apiFactory, apiReturnResults } from '../../ApiFactory';
import { type ApiResult, type BaseParams, Request } from '../../common';
import { links } from '../../links';

import {
    type CollectionFloorPriceItemResponse,
    type CollectionFloorPriceResponse,
    type CollectionListingsCount,
    type CollectionListingsCountResponse,
    type CollectionListingsItem,
    type CollectionListingsResponse,
    type CollectionMarketplaceSale,
    type CollectionMarketplaceSaleResponse,
    type CollectionResponse,
    type CollectionSalesByDateCount,
    type CollectionSalesByDateCountResponse,
    type CollectionSalesByPriceRangeCount,
    type CollectionSalesByPriceRangeCountResponse,
    type OwnersActivityListingsResponse,
    type OwnersActivitySalesResponse,
} from './types';

export async function getCollectionMetadataDetails(
    params: BaseParams & { slug: string },
): Promise<ApiResult<CollectionResponse>> {
    const { slug } = params;
    const target = links.collection().metadata;

    return Request.getRequest<CollectionResponse>({
        ...params,
        target,
        query: {
            ...params.query,
            slug,
            w: 1200, // For wider banner image
        },
    });
}

export const callGetCollectionMetadataDetails = (params: BaseParams & { slug: string }): Promise<CollectionResponse> =>
    apiFactory()
        .call(getCollectionMetadataDetails, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting collection ${params.slug}`;
            throw new Error(errorMessage);
        });

export async function getCollectionListings(
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number; limit: number },
): Promise<ApiResult<CollectionListingsResponse>> {
    return Request.getRequest<CollectionListingsResponse>({
        ...params,
        target: links
            .daas()
            .graphql()
            .collection()
            .byContract(params.contractAddress)
            .listings()
            .byPriceRange(params.minPriceWei, params.maxPriceWei)
            .active(params.limit),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callGetCollectionListings = (
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number; limit: number },
): Promise<CollectionListingsItem[]> =>
    apiFactory()
        .call(getCollectionListings, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value.active_ethereum_listings;
            }
            const errorMessage = result.error.detail ?? `Error getting collection listings`;
            throw new Error(errorMessage);
        });

export async function countCollectionFloorGameListings(
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number },
): Promise<ApiResult<CollectionListingsCountResponse>> {
    return Request.getRequest<CollectionListingsCountResponse>({
        ...params,
        target: links
            .daas()
            .graphql()
            .collection()
            .byContract(params.contractAddress)
            .listings()
            .byPriceRange(params.minPriceWei, params.maxPriceWei)
            .total(),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callCountCollectionListings = (
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number },
): Promise<CollectionListingsCount> =>
    apiFactory()
        .call(countCollectionFloorGameListings, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value.active_ethereum_listings_aggregate.aggregate.count;
            }
            const errorMessage = result.error.detail ?? `Error getting collection listings count`;
            throw new Error(errorMessage);
        });

export async function countCollectionSalesByPriceRange(
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number },
): Promise<ApiResult<CollectionSalesByPriceRangeCountResponse>> {
    return Request.getRequest<CollectionSalesByPriceRangeCountResponse>({
        ...params,
        target: links
            .daas()
            .graphql()
            .collection()
            .byContract(params.contractAddress)
            .sales()
            .byPriceRange(params.minPriceWei, params.maxPriceWei)
            .total(),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callCountCollectionSalesByPriceRange = (
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number },
): Promise<CollectionSalesByPriceRangeCount> =>
    apiFactory()
        .call(countCollectionSalesByPriceRange, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value.marketplace_sale_aggregate.aggregate.count;
            }
            const errorMessage = result.error.detail ?? `Error getting collection sales count`;
            throw new Error(errorMessage);
        });

export async function countCollectionSalesByDate(
    params: BaseParams & { contractAddress: string; dateFrom: Date },
): Promise<ApiResult<CollectionSalesByDateCountResponse>> {
    const isoDateTime = convertToIsoDateTime(params.dateFrom);
    return Request.getRequest<CollectionSalesByDateCountResponse>({
        ...params,
        target: links
            .daas()
            .graphql()
            .collection()
            .byContract(params.contractAddress)
            .sales()
            .byDate(isoDateTime)
            .total(),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callCountCollectionSalesByDate = (
    params: BaseParams & { contractAddress: string; dateFrom: Date },
): Promise<CollectionSalesByDateCount> =>
    apiFactory()
        .call(countCollectionSalesByDate, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value.marketplace_sale_aggregate.aggregate.count;
            }
            const errorMessage = result.error.detail ?? `Error getting collection sales count`;
            throw new Error(errorMessage);
        });

export async function getCollectionSalesByPriceAndDate(
    params: BaseParams & { contractAddress: string; dateFrom: Date; floorPriceWei: number },
): Promise<ApiResult<CollectionMarketplaceSaleResponse>> {
    const isoDateTime = convertToIsoDateTime(params.dateFrom);
    return Request.getRequest<CollectionMarketplaceSaleResponse>({
        ...params,
        target: links
            .daas()
            .graphql()
            .collection()
            .byContract(params.contractAddress)
            .sales()
            .recentTierSales(isoDateTime, params.floorPriceWei),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callGetCollectionByPriceAndDate = (
    params: BaseParams & { contractAddress: string; dateFrom: Date; floorPriceWei: number },
): Promise<CollectionMarketplaceSale[]> =>
    apiFactory()
        .call(getCollectionSalesByPriceAndDate, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value.marketplace_sale;
            }
            const errorMessage = result.error.detail ?? `Error getting collection sales`;
            throw new Error(errorMessage);
        });

export async function getCollectionFloorPriceBySlug(
    params: BaseParams & { slug: string },
): Promise<ApiResult<CollectionFloorPriceResponse>> {
    return Request.getRequest<CollectionFloorPriceResponse>({
        ...params,
        target: links.daas().graphql().collection().bySlug(params.slug).floorPrice(),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callGetCollectionFloorPriceBySlug = (params: BaseParams & { slug: string }) =>
    apiReturnResults<CollectionFloorPriceItemResponse>(getCollectionFloorPriceBySlug, params);

export const getOwnersActivitySales = (
    params: BaseParams & { slug: string },
): Promise<ApiResult<OwnersActivitySalesResponse>> => {
    const { slug } = params;
    const target = links.daas().marketplace().collections().activity(slug).sales;

    return Request.getRequest<OwnersActivitySalesResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

export const callGetOwnersActivitySales = async (params): Promise<OwnersActivitySalesResponse> => {
    const result_1 = await apiFactory().call(getOwnersActivitySales, { ...params }, { publicRequest: true });
    if (result_1.ok) {
        return result_1.value;
    }
    const errorMessage =
        result_1?.error?.detail && typeof result_1.error.detail === 'string'
            ? result_1.error.detail
            : `Error getting collection floor game listings`;
    throw new Error(errorMessage);
};

export const getOwnersActivityListings = (
    params: BaseParams & { slug: string },
): Promise<ApiResult<OwnersActivityListingsResponse>> => {
    const { slug } = params;
    const target = links.daas().marketplace().collections().activity(slug).listings;

    return Request.getRequest<OwnersActivityListingsResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

export const callGetOwnersActivityListings = (params: BaseParams & { slug?: string }) =>
    apiReturnResults<OwnersActivityListingsResponse>(getOwnersActivityListings, params, true);
